"use client";

import { lazy } from "react";
import type { ToolUIMeta, ToolUIRegistry } from "../../tool-renderer";

/**
 * Views for the `mailbox` capability. The view files themselves stay in
 * `../ms-graph/mail-views` because they share the message-list and
 * attachment rendering with each other — only registry ownership moves here,
 * mirroring the server split.
 *
 * The mail write tools have no custom view: drafts and filing render through
 * the HITL approval card, and their results narrate as text.
 */
export const mailboxToolRegistry = {
  search_mailbox: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("../ms-graph/mail-views").then(({ SearchMailboxToolView }) => ({
        default: SearchMailboxToolView,
      }))
    ),
  },
  list_recent_emails: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("../ms-graph/mail-views").then(({ ListRecentEmailsToolView }) => ({
        default: ListRecentEmailsToolView,
      }))
    ),
  },
  get_message: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("../ms-graph/mail-views").then(({ GetMessageToolView }) => ({
        default: GetMessageToolView,
      }))
    ),
  },
  get_internet_headers: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("../ms-graph/mail-views").then(
        ({ GetInternetHeadersToolView }) => ({
          default: GetInternetHeadersToolView,
        })
      )
    ),
  },
  list_attachments: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("../ms-graph/mail-views").then(({ ListAttachmentsToolView }) => ({
        default: ListAttachmentsToolView,
      }))
    ),
  },
  read_attachment: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("../ms-graph/mail-views").then(({ ReadAttachmentToolView }) => ({
        default: ReadAttachmentToolView,
      }))
    ),
  },
} satisfies ToolUIRegistry;

/**
 * Namespaced variant keyed by the progressive-disclosure inner-tool name
 * `mailbox_${toolName}`. Without this, a PD tool part resolves to no view and
 * falls back to `GenericToolView` — every email card in the transcript would
 * degrade to raw JSON. The bare-name entries above stay in the static tier
 * (still required by `AllToolNames` and used by chats persisted before the
 * split). See ADR-057.
 */
export const mailboxProgressiveDisclosureToolRegistry: Record<
  string,
  ToolUIMeta
> = Object.fromEntries(
  Object.entries(mailboxToolRegistry).map(([name, meta]) => [
    `mailbox_${name}`,
    meta,
  ])
);
